import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-common';
import { SilentRequest } from '@azure/msal-browser';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IOirRequest } from '../http/oir-http.service';
import { ConfigurationService, IAppSettings } from '../configuration/configuration.service';

export interface MSALTokenPayload {
  accessToken: string;
  scopes: string[];
}

export interface Account extends AccountInfo {
  idTokenClaims?: {
    oid?: string;
    roles?: string[];
  };
}

@Injectable({ providedIn: 'root' })
export class OirAuthService {
  constructor(
    private authService: MsalService,
    private config: ConfigurationService<IAppSettings>
  ) {}

  getAccessToken(request: IOirRequest<any> = {}): Observable<string> {
    return this.getAccessTokenByEndpoint(
      request.requestUrl === 'CommsEndpoint'
        ? 'CommsEndpoint'
        : request.requestUrl === 'ApiEndpoint2'
          ? 'ApiEndpoint2'
          : 'ApiEndpoint'
    );
  }

  getAccessTokenByEndpoint(
    endpoint: 'ApiEndpoint' | 'ApiEndpoint2' | 'CommsEndpoint'
  ): Observable<string> {
    const silentRequest = {
      account: this.getAccount(),
      forceRefresh: false,
      scopes: this.config.getValue(endpoint).scopes
    } as SilentRequest;

    return this.authService
      .acquireTokenSilent(silentRequest)
      .pipe(map((payload: MSALTokenPayload) => payload.accessToken));
  }

  getRoles(): string[] | null {
    const account: Account = this.getAccount();
    return account && account.idTokenClaims
      ? account.idTokenClaims.roles
      : null;
  }

  hasSomeRoles(checkRoles: string[]): boolean {
    if (!checkRoles || checkRoles.length < 1) {
      return false;
    }

    return checkRoles.some(role => this.hasRole(role));
  }

  hasEveryRole(checkRoles: string[]): boolean {
    if (!checkRoles || checkRoles.length < 1) {
      return false;
    }

    return checkRoles.every(role => this.hasRole(role));
  }

  hasRole(role: string): boolean {
    if (!role) {
      return false;
    }
    const accountRoles = this.getRoles();
    return accountRoles && accountRoles.length > 0
      ? accountRoles.includes(role)
      : false;
  }

  getUserId(): string {
    const account: Account = this.getAccount();
    return account ? account.localAccountId : '';
  }

  getUsername(): string {
    const account: Account = this.getAccount();
    return account ? account.username : '';
  }

  getFullname(): string {
    const account: Account = this.getAccount();
    return account ? account.name : '';
  }

  getAccount(): Account {
    return (
      this.authService.instance.getActiveAccount() ||
      this.authService.instance.getAllAccounts()[0]
    );
  }
}
